import Phaser from "phaser";
import { buildTileAtlas } from "@/assets/tileAtlas";
import { NPC_SHEETS } from "@/content/dialogs";
import { HERO_AVATARS } from "@/content/heroes";
import { MONSTER_SHEETS } from "@/content/monsters";
import { VIEW_H, VIEW_W } from "@/config";
import { TREE_SIZE } from "@/world/mapGen";

const FRAME = { frameWidth: 16, frameHeight: 16 };

/** Loads the Ninja Adventure sprites, tilesets and font, then composes the logical tile atlas. */
export class PreloadScene extends Phaser.Scene {
  constructor() {
    super("Preload");
  }

  preload(): void {
    const bar = this.add.graphics();
    this.load.on(Phaser.Loader.Events.PROGRESS, (p: number) => {
      bar.clear();
      bar.fillStyle(0x2f4858, 1);
      bar.fillRect(VIEW_W / 2 - 60, VIEW_H / 2 - 4, 120, 8);
      bar.fillStyle(0xffd54f, 1);
      bar.fillRect(VIEW_W / 2 - 60, VIEW_H / 2 - 4, Math.round(120 * p), 8);
    });

    this.load.setPath("assets/ninja");
    this.load.image("tileset-field", "tilesets/TilesetField.png");
    this.load.image("tileset-nature", "tilesets/TilesetNature.png");
    this.load.image("tileset-house", "tilesets/TilesetHouse.png");
    this.load.image("tileset-water", "tilesets/TilesetWater.png");
    this.load.image("tileset-camp", "tilesets/tileset_camp.png");
    this.load.image("tileset-element", "tilesets/TilesetElement.png");
    this.load.image("tileset-desert", "tilesets/TilesetDesert.png");
    this.load.image("font8x8", "fonts/font8x8.png");

    for (const a of Object.values(HERO_AVATARS)) this.load.spritesheet(a.texture, a.file, FRAME);
    for (const [key, file] of Object.entries(NPC_SHEETS))
      this.load.spritesheet(key, `characters/${file}`, FRAME);
    for (const [key, file] of Object.entries(MONSTER_SHEETS))
      this.load.spritesheet(key, `monsters/${file}`, FRAME);

    this.load.spritesheet("chest", "items/LittleTreasureChest.png", FRAME);
    this.load.spritesheet("bigchest", "items/BigTreasureChest.png", { frameWidth: 16, frameHeight: 14 });
  }

  create(): void {
    buildTileAtlas(this);
    this.registerSpriteFrames();
    this.registerFont();
    this.registerAnimations();
    this.scene.start("Title");
  }

  /**
   * Named frames for the trees, rocks and palms drawn as depth-sorted sprites: (x, y) in pixels on
   * the source tileset; the size comes from TREE_SIZE so the map generator and the scene agree.
   */
  private registerSpriteFrames(): void {
    const origins: Record<string, [number, number]> = {
      a: [0, 0],
      b: [32, 0],
      c: [96, 0],
      big_a: [0, 32],
      big_b: [64, 32],
      dead: [0, 80],
      round: [48, 288],
      autumn: [144, 288],
      pink: [0, 288],
      rock_tall: [240, 160],
      rock_huge: [272, 160],
      rock_wide: [336, 160],
      rock_tall_grey: [240, 224],
      rock_huge_grey: [272, 224],
      rock_wide_grey: [336, 224],
      palm_a: [160, 64],
      palm_b: [192, 64],
      palm_c: [160, 112],
    };
    for (const [kind, [x, y]] of Object.entries(origins)) {
      const def = TREE_SIZE[kind as keyof typeof TREE_SIZE];
      const tex = this.textures.get(def.texture);
      const name = `tree_${kind}`;
      if (!tex.has(name)) tex.add(name, 0, x, y, def.w, def.h);
    }
  }

  /** The pack's 8×8 bitmap font (printable ASCII in a 15-glyph-per-row sheet) as a Phaser retro font. */
  private registerFont(): void {
    if (this.cache.bitmapFont.has("pixel")) return;
    const config: Phaser.Types.GameObjects.BitmapText.RetroFontConfig = {
      image: "font8x8",
      width: 8,
      height: 8,
      chars: Phaser.GameObjects.RetroFont.TEXT_SET1,
      charsPerRow: 15,
      "offset.x": 0,
      "offset.y": 0,
      "spacing.x": 0,
      "spacing.y": 0,
      lineSpacing: 0,
    };
    this.cache.bitmapFont.add("pixel", Phaser.GameObjects.RetroFont.Parse(this, config));
  }

  private registerAnimations(): void {
    const anims = this.anims;
    const add = (key: string, texture: string, frames: number[], frameRate: number) => {
      if (anims.exists(key)) return;
      anims.create({ key, frames: anims.generateFrameNumbers(texture, { frames }), frameRate, repeat: -1 });
    };
    // character walk sheets: 4 columns per row, rows = down, up, left, right
    const dirs = ["down", "up", "left", "right"] as const;
    for (const a of Object.values(HERO_AVATARS)) {
      dirs.forEach((dir, row) =>
        add(`${a.texture}-walk-${dir}`, a.texture, [row * 4, row * 4 + 1, row * 4 + 2, row * 4 + 3], 8),
      );
    }
    // monsters: use the "down" row for the idle bounce
    for (const key of Object.keys(MONSTER_SHEETS)) add(`${key}-idle`, key, [0, 1, 2, 3], 6);
  }
}
